import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { VscChevronDown, VscChevronUp } from 'react-icons/vsc'

import {
  AccordionContainer,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
} from './ObjetivesSection/styles'
import TrackExercisePicker from './TrackExercisePicker'
import { withExerciseTrackPath, withSelectedTrack } from './trackPicker'
import { InlineMenu, SectionSubtitle, Textarea } from '../../../..'
import { useStorage, useUser } from '../../../../../context'
import { TrackAudioFile } from '../../../../../firebase/utils/storageActions'
import { LogExercices, NewLog } from '../../../../../types'
import { exerciseUsesLegacyTrackPicker } from '../../../../../utilFunctions/trackExerciseSchema'
import { SelectComponent } from '../../../../Select/Select'

import { ItemContainer } from './styles'

const ExerciseAccordion = ({
  data,
  setLogData,
}: {
  data: NewLog
  setLogData: Dispatch<SetStateAction<NewLog | null>>
}) => {
  const { onEditionMode } = useUser()
  const {
    audiosLists,
    loadingTracks,
    tracksAudioError,
    tracksAudioIndex,
    getAudioList,
  } = useStorage()
  const [activeList, setActiveList] = useState<
    { title: string; url: string }[] | null
  >(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const handleToggle = useCallback(
    (index: number) => {
      setOpenIndex(openIndex === index ? null : index)
    },
    [openIndex],
  )

  const handleUpdateLegacy = useCallback(
    (value: string | string[] | null, name: keyof LogExercices) => {
      if (openIndex === null) return

      const updatedEx = data.exercises.map((exc, i) =>
        i === openIndex
          ? {
              ...exc,
              [name]: value,
              ...(name !== 'category' && {
                exercise: { title: '', url: '' },
              }),
            }
          : exc,
      )

      setLogData({ ...data, exercises: updatedEx })
    },
    [data, openIndex, setLogData],
  )

  const updateExerciseAt = useCallback(
    (rowIndex: number, update: (exercise: LogExercices) => LogExercices) => {
      setLogData((prev) => {
        if (!prev) return prev

        return {
          ...prev,
          exercises: prev.exercises.map((exercise, index) =>
            index === rowIndex ? update(exercise) : exercise,
          ),
        }
      })
    },
    [setLogData],
  )

  const setTrackPathAt = useCallback(
    (rowIndex: number, path: string[]) => {
      updateExerciseAt(rowIndex, (exercise) =>
        withExerciseTrackPath(exercise, path),
      )
    },
    [updateExerciseAt],
  )

  const setTrackAt = useCallback(
    (rowIndex: number, track: TrackAudioFile | null) => {
      updateExerciseAt(rowIndex, (exercise) =>
        withSelectedTrack(exercise, track),
      )
    },
    [updateExerciseAt],
  )

  const handleChange = useCallback(
    (
      e:
        | ChangeEvent<HTMLSelectElement>
        | ChangeEvent<HTMLInputElement>
        | ChangeEvent<HTMLTextAreaElement>,
    ) => {
      const { value, name } = e.target
      if (openIndex === null) return

      const processedValue =
        name === 'exercise'
          ? (() => {
              const [title, url] = value.split(' | ')
              return { title, url }
            })()
          : value

      updateExerciseAt(openIndex, (exercise) => ({
        ...exercise,
        [name]: processedValue as never,
      }))
    },
    [openIndex, updateExerciseAt],
  )

  const filterAudioListLegacy = useCallback(
    (gender: string | null, routine: string | null) => {
      if (!audiosLists || !gender || !routine) {
        setActiveList(null)
        return
      }
      const routineIndex = {
        'Trabajo respiratorio': 1,
        'Estimulación laríngea': 0,
        'Trabajo resonancial': 2,
      }[routine]

      if (routineIndex === undefined) {
        setActiveList(null)
        return
      }

      const bucket = audiosLists[routineIndex]
      if (!bucket) {
        setActiveList(null)
        return
      }

      const list = gender === 'Masculino' ? bucket.hombre : bucket.mujer
      setActiveList(list?.length ? list : null)
    },
    [audiosLists],
  )

  const usesLegacyPicker = useCallback(
    (exercise: LogExercices) =>
      exerciseUsesLegacyTrackPicker(
        exercise,
        Object.keys(tracksAudioIndex?.folders ?? {}),
      ),
    [tracksAudioIndex],
  )

  useEffect(() => {
    if (openIndex === null) {
      setActiveList(null)
      return
    }
    const exercise = data.exercises[openIndex]
    if (usesLegacyPicker(exercise)) {
      filterAudioListLegacy(exercise.gender, exercise.routine)
    } else {
      setActiveList(null)
    }
  }, [
    openIndex,
    data.exercises,
    audiosLists,
    filterAudioListLegacy,
    usesLegacyPicker,
  ])

  const rowDisabled = (i: number) => !onEditionMode || openIndex !== i

  return (
    <AccordionContainer>
      {data.exercises.map((exer, i) => {
        const legacy = usesLegacyPicker(exer)

        return (
          <AccordionItem key={i}>
            <AccordionHeader
              onClick={() => handleToggle(i)}
              style={{
                border: '0.5px solid #8e92bc',
                boxShadow: '0px 4px 8px 0px rgba(94, 97, 127, 0.05)',
              }}
            >
              <div style={{ display: 'flex', gap: '36px' }}>
                <p>N {i + 1}</p>
                <p>Ejercicio</p>
              </div>
              {openIndex === i ? <VscChevronUp /> : <VscChevronDown />}
            </AccordionHeader>
            <AccordionContent $isOpen={openIndex === i} style={{ gap: '24px' }}>
              {onEditionMode && (
                <SectionSubtitle
                  fontSize={14}
                  fontWeight={500}
                  classSub="section-title"
                >
                  <>
                    <span>1</span>
                    Seleccionar ejercicio
                  </>
                </SectionSubtitle>
              )}
              {legacy ? (
                <>
                  <ItemContainer>
                    <label>Categoría del ejercicio (bitácora anterior)</label>
                    <InlineMenu
                      menuName="trainingType"
                      activeFilter={exer.category}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'category')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                  <ItemContainer>
                    <label>Género del ejercicio</label>
                    <InlineMenu
                      menuName="exerciseGenre"
                      activeFilter={exer.gender}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'gender')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                  <ItemContainer>
                    <label>Tipo de entrenamiento</label>
                    <InlineMenu
                      menuName="routineType"
                      activeFilter={exer.routine}
                      setActiveFilter={(value) =>
                        handleUpdateLegacy(value, 'routine')
                      }
                      isDisabled={rowDisabled(i)}
                    />
                  </ItemContainer>
                </>
              ) : (
                <TrackExercisePicker
                  disabled={rowDisabled(i)}
                  exercise={exer}
                  index={tracksAudioIndex}
                  loading={loadingTracks}
                  error={tracksAudioError}
                  onRetry={getAudioList}
                  onPathChange={(path) => setTrackPathAt(i, path)}
                  onTrackChange={(track) => setTrackAt(i, track)}
                />
              )}

              {legacy &&
                openIndex === i &&
                activeList &&
                activeList.length > 0 && (
                  <ItemContainer>
                    <label>Selecciona el ejercicio</label>

                    <SelectComponent
                      value={exer.exercise?.title || ''}
                      onChange={handleChange}
                      name="exercise"
                      disabled={!onEditionMode}
                    >
                      <option value="">
                        {exer.exercise?.title
                          ? exer.exercise?.title.replace('.mp3', '')
                          : 'Seleccionar audio'}
                      </option>

                      {activeList.map(({ title, url }, idx) => (
                        <option key={idx} value={`${title} | ${url}`}>
                          {title.replace('.mp3', '')}
                        </option>
                      ))}
                    </SelectComponent>
                  </ItemContainer>
                )}
              <ItemContainer>
                <label>Observaciones</label>
                <Textarea
                  name="observation"
                  id="observation"
                  onChange={handleChange}
                  placeholder="Agregar observación"
                  readOnly={!onEditionMode}
                  defaultValue={exer.observation || ''}
                />
              </ItemContainer>
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </AccordionContainer>
  )
}

export default ExerciseAccordion
